import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, User } from "lucide-react";
import { uploadImageToCloudflare } from "../../api/cloudflareUpload";
import { getInvalidUploadFilenameMessage, isUploadFilenameValid } from "../../utils/fileUploadValidation";

const ProfilePhotoCard = ({ imageUrl, isEditing, onUploaded, onUploadError, onUploadingChange }) => {
    const { t } = useTranslation();
    const [preview, setPreview] = useState(imageUrl);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!isUploadFilenameValid(file.name)) {
            onUploadError?.(getInvalidUploadFilenameMessage());
            event.target.value = "";
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);

        setIsUploading(true);
        setUploadProgress(0);
        onUploadingChange?.(true);
        try {
            const result = await uploadImageToCloudflare(file, setUploadProgress);
            onUploaded?.(result);
        } catch (error) {
            onUploadError?.(error?.message || t("ACCOUNT_ERROR_UPLOAD_PROFILE_PHOTO"));
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
            onUploadingChange?.(false);
        }
    };

    const handleUploadClick = () => {
        if (!isEditing) return;
        fileInputRef.current?.click();
    };

    return (
        <div>
            <p className="text-sm font-semibold text-slate-900">{t("ACCOUNT_PROFILE_PHOTO")}</p>
            <div className="relative w-fit">
                <div className="mt-4 h-32 w-32 overflow-hidden rounded-2xl bg-slate-100 flex items-center justify-center">
                    {preview ? (
                        <img
                            src={preview}
                            alt=""
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <User className="h-14 w-14 text-slate-400" />
                    )}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                />
                {isEditing ? (
                    <button
                        type="button"
                        onClick={handleUploadClick}
                        disabled={isUploading}
                    className="absolute z-40 -bottom-2 -right-2 flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-sm font-semibold text-slate-600 shadow-md transition hover:scale-105 focus:outline-none"
                    >
                        {isUploading ? t("COMMON_UPLOADING_SHORT") : preview ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    </button>
                ) : null}
            </div>
            {isUploading && (
                <div className="mt-3 w-32">
                    <div className="mb-1 flex items-center justify-between">
                        <p className="text-xs text-slate-500">{t("COMMON_UPLOADING")}</p>
                        <p className="text-xs font-medium text-primary">{uploadProgress}%</p>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-slate-200">
                        <div
                            className="h-1.5 rounded-full bg-primary transition-all duration-200"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfilePhotoCard;
